import React, { useState } from "react";
import { Box } from "@mui/material";
import styled from "styled-components";
import { PrimaryButton } from "../Button";

// Estilização premium para o container do Editor
const EditorContainer = styled(Box)`
  margin-top: 2rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  padding: 1.5rem;
  border: 1px solid #e0f2f1;
`;

// Switch de Abas Moderno (Editar vs Visualizar)
const TabHeader = styled.div`
  display: flex;
  border-bottom: 2px solid #e0f2f1;
  gap: 1.5rem;
`;

const TabButton = styled.button`
  background: none;
  border: none;
  font-size: 1rem;
  font-weight: 600;
  padding: 0.75rem 0.5rem;
  cursor: pointer;
  color: ${props => props.active ? "#03738c" : "#757575"};
  border-bottom: 3px solid ${props => props.active ? "#03738c" : "transparent"};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  bottom: -2px;

  &:hover {
    color: #004d40;
  }
`;

// Área de conteúdo da aba
const TabContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 400px;
`;

// Textarea estilizado de forma premium
const MarkdownTextarea = styled.textarea`
  width: 100%;
  height: 100%;
  min-height: 400px;
  border: 1px solid #b2dfdb;
  border-radius: 8px;
  padding: 1.25rem;
  font-family: 'Fira Code', 'JetBrains Mono', 'Courier New', Courier, monospace;
  font-size: 0.95rem;
  line-height: 1.6;
  color: #263238;
  resize: vertical;
  outline: none;
  background-color: #fafafa;
  transition: all 0.3s ease;

  &:focus {
    border-color: #03738c;
    box-shadow: 0 0 0 3px rgba(3, 115, 140, 0.15);
    background-color: #ffffff;
  }
`;

// Renderizador da Visualização de Markdown Formatado
const PreviewContainer = styled.div`
  width: 100%;
  height: 100%;
  min-height: 400px;
  border: 1px solid #e0f2f1;
  border-radius: 8px;
  padding: 1.25rem;
  background-color: #fafdfd;
  overflow-y: auto;
  font-family: 'Inter', system-ui, sans-serif;
  color: #37474f;
  line-height: 1.8;

  h1, h2, h3 {
    color: #03738c;
    margin-top: 1.5rem;
    margin-bottom: 0.75rem;
    font-weight: 700;
  }

  h1 {
    font-size: 1.8rem;
    border-bottom: 2px solid #e0f2f1;
    padding-bottom: 0.5rem;
  }

  h2 {
    font-size: 1.4rem;
  }

  h3 {
    font-size: 1.15rem;
  }

  p {
    margin-bottom: 1rem;
  }

  strong {
    color: #004d40;
    font-weight: 600;
  }

  em {
    font-style: italic;
  }

  br {
    display: block;
    margin: 0.5rem 0;
    content: "";
  }
`;

// Função simples de parsing de markdown para HTML
function renderMarkdownToHtml(markdown) {
  if (!markdown) return "";
  
  // Escapar HTML básico para proteção contra XSS
  let html = markdown
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  // Parser de Títulos
  html = html.replace(/^### (.*$)/gim, "<h3>$1</h3>");
  html = html.replace(/^## (.*$)/gim, "<h2>$1</h2>");
  html = html.replace(/^# (.*$)/gim, "<h1>$1</h1>");

  // Parser de Negrito
  html = html.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

  // Parser de Itálico
  html = html.replace(/\*(.*?)\*/g, "<em>$1</em>");
  html = html.replace(/_(.*?)_/g, "<em>$1</em>");

  // Parser de Parágrafos e quebras de linha
  html = html.split("\n").map(line => {
    const trimmed = line.trim();
    if (!trimmed) return "<br />";
    if (trimmed.startsWith("<h") || trimmed.startsWith("</h")) return trimmed;
    return `<p>${trimmed}</p>`;
  }).join("\n");

  return html;
}

function Index({ terms, setTerms, saveTerms, email, whatsapp, valueTextArea }) {
  const [activeTab, setActiveTab] = useState("edit");
  
  // O valor do texto pode vir da propriedade terms (se for string) ou de valueTextArea
  const textValue = typeof terms === "string" ? terms : valueTextArea || "";

  const isCorrectForm = !!email && !!textValue;

  const handleTextareaChange = (event) => {
    setTerms(event.target.value);
  };

  return (
    <EditorContainer>
      <TabHeader>
        <TabButton 
          type="button" 
          active={activeTab === "edit"} 
          onClick={() => setActiveTab("edit")}
        >
          Editar
        </TabButton>
        <TabButton 
          type="button" 
          active={activeTab === "preview"} 
          onClick={() => setActiveTab("preview")}
        >
          Visualizar
        </TabButton>
      </TabHeader>

      <TabContent>
        {activeTab === "edit" ? (
          <MarkdownTextarea
            value={textValue}
            onChange={handleTextareaChange}
            placeholder="Digite os termos ou política em formato Markdown... (Ex: # Título, **Negrito**)"
          />
        ) : (
          <PreviewContainer 
            dangerouslySetInnerHTML={{ __html: renderMarkdownToHtml(textValue) }} 
          />
        )}
      </TabContent>

      <PrimaryButton
        disabled={!isCorrectForm}
        style={{ justifySelf: "end", alignSelf: "flex-end", padding: "8px 32px" }}
        onClick={saveTerms}
      >
        Salvar
      </PrimaryButton>
    </EditorContainer>
  );
}

export default Index;
